import type {
  StatsRangeKey,
  StatsServiceNoteNote,
  StatsServiceNoteResponse,
  StatsServiceNoteReason,
} from "./statsDashboardTypes";

function asRecord(value: unknown): Record<string, unknown> {
  if (!value || typeof value !== "object" || Array.isArray(value)) return {};
  return value as Record<string, unknown>;
}

function asArray(value: unknown): unknown[] {
  return Array.isArray(value) ? value : [];
}

function asString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function asBoolean(value: unknown): boolean {
  return value === true;
}

function normalizeNote(value: unknown): StatsServiceNoteNote | null {
  const record = asRecord(value);
  const headline = asString(record["headline"]);
  if (!headline) return null;

  return {
    headline,
    executivePoints: asArray(record["executive_points"])
      .map((item) => asString(item))
      .filter((item): item is string => !!item),
    centerReadouts: asArray(record["center_readouts"])
      .map((item) => {
        const row = asRecord(item);
        const center = asString(row["center"]);
        const text = asString(row["text"]);
        if (!center || !text) return null;
        return { center, text };
      })
      .filter((item): item is { center: string; text: string } => !!item),
    watchouts: asArray(record["watchouts"])
      .map((item) => asString(item))
      .filter((item): item is string => !!item),
    limitations: asArray(record["limitations"])
      .map((item) => asString(item))
      .filter((item): item is string => !!item),
  };
}

function normalizeReason(value: unknown): StatsServiceNoteReason {
  if (value === "ok" || value === "not_generated_yet" || value === "unsupported_filters") {
    return value;
  }
  return "not_generated_yet";
}

export function normalizeStatsServiceNoteResponse(value: unknown): StatsServiceNoteResponse {
  const record = asRecord(value);

  return {
    available: asBoolean(record["available"]),
    officialView: asBoolean(record["official_view"]),
    reason: normalizeReason(record["reason"]),
    generatedAt: asString(record["generated_at"]),
    note: normalizeNote(record["note_json"]),
    markdown: asString(record["note_markdown"]),
  };
}

export function buildStatsServiceNoteFilename(params: {
  brand: string;
  rangeKey: StatsRangeKey;
  center: string | null;
}): string {
  const scope = params.center ? params.center : "global";
  const sanitizedScope = scope
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .toLowerCase();

  return `note-service-stats-${params.brand}-${params.rangeKey}-${sanitizedScope || "global"}.md`;
}
